import React from 'react';
import { View, Text } from 'react-native';
import { getDeployTarget } from '../../config/environment';
import { AdminToggle } from './AdminToggle';

function FlagCard({
  title,
  description,
  stagingValue,
  productionValue,
  onStagingChange,
  onProductionChange,
  adminStyles,
  colors,
}) {
  return (
    <View
      style={[
        adminStyles.devModeSection,
        { flex: 1, minWidth: 300, marginTop: 0, marginBottom: 0 },
      ]}
    >
      <Text style={[adminStyles.cardTitle, { fontSize: 16 }]}>{title}</Text>
      <View style={adminStyles.devModeContent}>
        <Text style={[adminStyles.devModeText, { fontSize: 13, lineHeight: 19 }]}>{description}</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
          <View
            style={{
              flex: 1,
              minWidth: 120,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingVertical: 8,
              paddingHorizontal: 10,
              backgroundColor: colors.cardBgSubtle,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: colors.cardBorder,
            }}
          >
            <Text style={{ color: colors.textPrimary, fontWeight: '600', fontSize: 13 }}>Staging</Text>
            <AdminToggle value={stagingValue} onValueChange={onStagingChange} />
          </View>
          <View
            style={{
              flex: 1,
              minWidth: 120,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingVertical: 8,
              paddingHorizontal: 10,
              backgroundColor: colors.cardBgSubtle,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: colors.cardBorder,
            }}
          >
            <Text style={{ color: colors.textPrimary, fontWeight: '600', fontSize: 13 }}>Production</Text>
            <AdminToggle value={productionValue} onValueChange={onProductionChange} />
          </View>
        </View>
      </View>
    </View>
  );
}

export function AdminFeatureFlags({ featureFlags, persistFeatureFlag, persistTourPlannerFlag, adminStyles, colors }) {
  const flags = [
    {
      title: 'Account',
      description: 'Account / Profile screen and hamburger Account. No notify-me gate when on. Staging and production are both on.',
      staging: featureFlags.accountVisibleStaging !== false,
      production: featureFlags.accountVisibleProduction !== false,
      onStaging: (v) => persistFeatureFlag('accountVisibleStaging', v),
      onProduction: (v) => persistFeatureFlag('accountVisibleProduction', v),
    },
    {
      title: 'Mutual Aid Fund',
      description: 'Fund page at /fund and hamburger Fund. Off shows Notify me when this is live. Admins always see this.',
      staging: featureFlags.fundVisibleStaging !== false,
      production: featureFlags.fundVisibleProduction === true,
      onStaging: (v) => persistFeatureFlag('fundVisibleStaging', v),
      onProduction: (v) => persistFeatureFlag('fundVisibleProduction', v),
    },
    {
      title: 'Events',
      description: 'Hamburger Events. Off shows Notify me when this is live. Admins always see this.',
      staging: featureFlags.eventsVisibleStaging !== false,
      production: featureFlags.eventsVisibleProduction === true,
      onStaging: (v) => persistFeatureFlag('eventsVisibleStaging', v),
      onProduction: (v) => persistFeatureFlag('eventsVisibleProduction', v),
    },
    {
      title: 'Gear Swap',
      description: 'Gear Swap screens and hamburger Gear. Off shows Notify me when this is live. Admins always see this.',
      staging: featureFlags.gearVisibleStaging !== false,
      production: featureFlags.gearVisibleProduction === true,
      onStaging: (v) => persistFeatureFlag('gearVisibleStaging', v),
      onProduction: (v) => persistFeatureFlag('gearVisibleProduction', v),
    },
    {
      title: 'Tour Planner',
      description: 'Header map, My Tours, calendar links. Off shows Notify me when this is live. Admins/dev always see this.',
      staging: featureFlags.tourPlannerVisibleStaging !== false,
      production: featureFlags.tourPlannerVisibleProduction === true,
      onStaging: (v) => persistTourPlannerFlag('tourPlannerVisibleStaging', v),
      onProduction: (v) => persistTourPlannerFlag('tourPlannerVisibleProduction', v),
    },
    {
      title: 'Community Directory',
      description: 'Community icon and directory. Off shows Notify me when this is live. Admins/dev always see this.',
      staging: featureFlags.communityVisibleStaging !== false,
      production: featureFlags.communityVisibleProduction === true,
      onStaging: (v) => persistFeatureFlag('communityVisibleStaging', v),
      onProduction: (v) => persistFeatureFlag('communityVisibleProduction', v),
    },
    {
      title: 'Calendar',
      description: 'My Calendar screen and nav button. Off shows Notify me when this is live. Admins/dev always see this.',
      staging: featureFlags.calendarVisibleStaging !== false,
      production: featureFlags.calendarVisibleProduction === true,
      onStaging: (v) => persistFeatureFlag('calendarVisibleStaging', v),
      onProduction: (v) => persistFeatureFlag('calendarVisibleProduction', v),
    },
    {
      title: 'Budget (Expense Tracker)',
      description: 'Hamburger Budget and the expense tracker. Signed-in users only: guests who tap it are asked to sign in. Off shows Notify me when this is live. Admins/dev always see this.',
      staging: featureFlags.expenseTrackerVisibleStaging !== false,
      production: featureFlags.expenseTrackerVisibleProduction === true,
      onStaging: (v) => persistFeatureFlag('expenseTrackerVisibleStaging', v),
      onProduction: (v) => persistFeatureFlag('expenseTrackerVisibleProduction', v),
    },
    {
      title: 'Budget revisions (Oct 21 meeting)',
      description: 'New Budget layout: tour picker first, per-tour saving, merch on its own page, no % of sales seller fee. Off keeps the current Budget. Applies to everyone, admins included, so you see exactly what users see.',
      staging: featureFlags.budgetRevisionsStaging !== false,
      production: featureFlags.budgetRevisionsProduction === true,
      onStaging: (v) => persistFeatureFlag('budgetRevisionsStaging', v),
      onProduction: (v) => persistFeatureFlag('budgetRevisionsProduction', v),
    },
    {
      title: 'Wallet',
      description: 'Wallet page and hamburger Wallet. Lauren: keep hidden until Budget is live and the Map is viewable. Off shows Notify me when this is live. Admins always see this.',
      staging: featureFlags.walletVisibleStaging !== false,
      production: featureFlags.walletVisibleProduction === true,
      onStaging: (v) => persistFeatureFlag('walletVisibleStaging', v),
      onProduction: (v) => persistFeatureFlag('walletVisibleProduction', v),
    },
    {
      title: 'Centered Nav',
      description: 'Logo centered with icons cascading outward on each side.',
      staging: featureFlags.centeredNavStaging !== false,
      production: featureFlags.centeredNavProduction === true,
      onStaging: (v) => persistFeatureFlag('centeredNavStaging', v),
      onProduction: (v) => persistFeatureFlag('centeredNavProduction', v),
    },
    {
      title: 'Grid Nav',
      description: '2×3 icon grid left, title center, profile right. Overrides centered nav.',
      staging: featureFlags.gridNavStaging === true,
      production: featureFlags.gridNavProduction === true,
      onStaging: (v) => persistFeatureFlag('gridNavStaging', v),
      onProduction: (v) => persistFeatureFlag('gridNavProduction', v),
    },
    {
      title: 'Stacked Center Nav',
      description: 'Title + icon row centered; profile/welcome on the right. Overrides grid and centered nav.',
      staging: featureFlags.stackedCenterNavStaging === true,
      production: featureFlags.stackedCenterNavProduction === true,
      onStaging: (v) => persistFeatureFlag('stackedCenterNavStaging', v),
      onProduction: (v) => persistFeatureFlag('stackedCenterNavProduction', v),
    },
    {
      title: 'Simple Center Nav',
      description: 'Hamburger left, centered title, profile right — no icon grid. Overrides other nav layouts.',
      staging: featureFlags.simpleCenterNavStaging === true,
      production: featureFlags.simpleCenterNavProduction === true,
      onStaging: (v) => persistFeatureFlag('simpleCenterNavStaging', v),
      onProduction: (v) => persistFeatureFlag('simpleCenterNavProduction', v),
    },
    {
      title: 'Left Logo Nav',
      description: '2×3 icon grid on the left (logo first, same Ionicons as the current header, no green squares), marker-font VENUE REPORT CARD filling the middle. Overrides other nav layouts.',
      staging: featureFlags.leftLogoNavStaging !== false,
      production: featureFlags.leftLogoNavProduction === true,
      onStaging: (v) => persistFeatureFlag('leftLogoNavStaging', v),
      onProduction: (v) => persistFeatureFlag('leftLogoNavProduction', v),
    },
    {
      title: 'Updated Color Scheme',
      description: 'Canva page colors for venue, gear swap, and calendar (headers, backgrounds, footers). Off restores originals.',
      staging: featureFlags.updatedColorSchemeStaging === true,
      production: featureFlags.updatedColorSchemeProduction === true,
      onStaging: (v) => persistFeatureFlag('updatedColorSchemeStaging', v),
      onProduction: (v) => persistFeatureFlag('updatedColorSchemeProduction', v),
    },
    {
      title: 'Hide Nav On Scroll',
      description: 'Fixed header slides away on scroll-down; reappears on scroll-up or when hovering the top edge.',
      staging: featureFlags.hideNavOnScrollStaging !== false,
      production: featureFlags.hideNavOnScrollProduction === true,
      onStaging: (v) => persistFeatureFlag('hideNavOnScrollStaging', v),
      onProduction: (v) => persistFeatureFlag('hideNavOnScrollProduction', v),
    },
    {
      title: 'Fixed Footer',
      description: 'Pin the site footer to the bottom of the viewport on every page.',
      staging: featureFlags.fixedFooterStaging !== false,
      production: featureFlags.fixedFooterProduction === true,
      onStaging: (v) => persistFeatureFlag('fixedFooterStaging', v),
      onProduction: (v) => persistFeatureFlag('fixedFooterProduction', v),
    },
    {
      title: 'User Locations (Tour Planner)',
      description: 'User Locations pill and show-on-map toggle in Tour Planner filters. Staging also controls local development.',
      staging: featureFlags.userLocationsVisibleStaging !== false,
      production: featureFlags.userLocationsVisibleProduction !== false,
      onStaging: (v) => persistFeatureFlag('userLocationsVisibleStaging', v),
      onProduction: (v) => persistFeatureFlag('userLocationsVisibleProduction', v),
    },
    {
      title: 'Footer Resources',
      description: 'Show accessibility, safety, environment, and education icons in the site footer. Off hides them completely (default).',
      staging: featureFlags.footerResourcesVisibleStaging === true,
      production: featureFlags.footerResourcesVisibleProduction === true,
      onStaging: (v) => persistFeatureFlag('footerResourcesVisibleStaging', v),
      onProduction: (v) => persistFeatureFlag('footerResourcesVisibleProduction', v),
    },
    {
      title: 'Master Tour Calendar UI',
      description: 'Client calendar layout (month day-cards, week itinerary, day-sheet poster) and matching map UX (Pins rail, food hubs). Off restores the previous calendar and map chrome. Staging also controls local development. Production is on.',
      staging: featureFlags.masterTourCalendarUiStaging !== false,
      production: featureFlags.masterTourCalendarUiProduction === true,
      onStaging: (v) => persistFeatureFlag('masterTourCalendarUiStaging', v),
      onProduction: (v) => persistFeatureFlag('masterTourCalendarUiProduction', v),
    },
    {
      title: 'Header About / Contact / Donate',
      description: 'Canva header links under the title (About, Contact, Donate). Off by default on staging, production, and local. Footer links stay. Staging also controls local development.',
      staging: featureFlags.canvaHeaderLinksStaging === true,
      production: featureFlags.canvaHeaderLinksProduction === true,
      onStaging: (v) => persistFeatureFlag('canvaHeaderLinksStaging', v),
      onProduction: (v) => persistFeatureFlag('canvaHeaderLinksProduction', v),
    },
    {
      title: 'Tour community hub',
      description: 'Admin-style account dropdown (Venue Database / Add / Grade / Budget / Fund), tour party chats in Discussions → Messages, and email notifications for new channel messages. Staging also controls local development. Production stays off until you flip it.',
      staging: featureFlags.tourCommunityHubStaging !== false,
      production: featureFlags.tourCommunityHubProduction === true,
      onStaging: (v) => persistFeatureFlag('tourCommunityHubStaging', v),
      onProduction: (v) => persistFeatureFlag('tourCommunityHubProduction', v),
    },
  ];

  return (
    <View>
      <View
        style={{
          paddingVertical: 10,
          paddingHorizontal: 14,
          marginBottom: 16,
          borderRadius: 10,
          backgroundColor: colors.cardBgSubtle,
          borderWidth: 1,
          borderColor: colors.cardBorder,
        }}
      >
        <Text style={{ color: colors.textSecondary, fontSize: 13 }}>
          Deploy target for this session:{' '}
          <Text style={{ fontWeight: '700', color: colors.textPrimary }}>{getDeployTarget()}</Text>
          {' '}(hostname / REACT_APP_DEPLOY_TARGET). Staging and production toggles apply independently.
        </Text>
      </View>

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
        {flags.map((flag) => (
          <FlagCard
            key={flag.title}
            title={flag.title}
            description={flag.description}
            stagingValue={flag.staging}
            productionValue={flag.production}
            onStagingChange={flag.onStaging}
            onProductionChange={flag.onProduction}
            adminStyles={adminStyles}
            colors={colors}
          />
        ))}
      </View>
    </View>
  );
}
